import Vue from 'vue';
import VueI18n from 'vue-i18n';
import messages from '../messages';
import { get, ENDPOINTS } from '@/api/mainApi';
import dayjs from 'dayjs/esm/index.js';
import relativeTime from 'dayjs/esm/plugin/relativeTime/index.js';
import localizedFormat from 'dayjs/esm/plugin/localizedFormat/index.js';
import locale_sv from 'dayjs/esm/locale/sv.js';
import locale_nb from 'dayjs/esm/locale/nb.js';
import locale_de from 'dayjs/esm/locale/de.js';
import locale_en from 'dayjs/esm/locale/en.js';
import { TranslatableEntity } from '@/interfaces/translatableEntity';

Vue.use(VueI18n);

function getSupportedLocale(): string {
  const supportedLocales = ['sv-SE', 'nb-NO', 'de-DE', 'en'];
  const browserLanguages = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];

  for (let browserLanguage of browserLanguages) {
    const browserLanguageIndex: number = browserLanguages.indexOf(browserLanguage);
    const normalizedBrowserLanguage: string = browserLanguage.toLowerCase();

    const exactMatch: string | undefined = supportedLocales.find(locale => locale.toLowerCase() === normalizedBrowserLanguage);
    if (hasValidLanguageMatch(exactMatch, browserLanguageIndex)) {
      return exactMatch!;
    }
    const partialMatch: string | undefined = supportedLocales.find(locale => locale.toLowerCase().indexOf(normalizedBrowserLanguage) > -1);
    if (hasValidLanguageMatch(partialMatch, browserLanguageIndex)) {
      return partialMatch!;
    }
    const languageOnly: string = normalizedBrowserLanguage.split('-')[0];
    const languageMatch: string | undefined = supportedLocales.find(locale => locale.split('-')[0].toLowerCase() === languageOnly);
    if (hasValidLanguageMatch(languageMatch, browserLanguageIndex)) {
      return languageMatch!;
    }
  }

  return 'sv-SE';
}

function hasValidLanguageMatch(matchingLocale: string | undefined, browserLanguageIndex: number): boolean {
  // english is often included by default in the browser languages (but not at the top)
  // so we only want to prioritize it if it's the first language in the list
  if (!!matchingLocale) {
    return (matchingLocale !== 'en' || (matchingLocale === 'en' && browserLanguageIndex === 0))
  }
  return false;
}

const supportedLocale = getSupportedLocale();
const minimumTranslationLength = 30; // counts json nodes / categories

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

if (supportedLocale === 'sv-SE') {
  dayjs.locale(locale_sv);
} else if (supportedLocale === 'nb-NO') {
  dayjs.locale(locale_nb);
} else if (supportedLocale === 'de-DE') {
  dayjs.locale(locale_de);
} else if (supportedLocale === 'en') {
  dayjs.locale(locale_en);
} else {
  dayjs.locale(locale_sv);
}

Object.defineProperties(Vue.prototype, {
  $date: {
    get() {
      return dayjs
    }
  },
});

const dateTimeFormats = {
  weekDay: {
    weekday: 'long',
  },
  yearMonth: {
    year: 'numeric',
    month: 'long',
  },
};

const numberFormats = {
  'sv-SE': {
    currency: {
      style: 'currency', currency: 'SEK'
    },
    decimal: {
      style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2
    },
    percent: {
      style: 'percent', useGrouping: false
    }
  },
  'nb-NO': {
    currency: {
      style: 'currency', currency: 'NOK', useGrouping: true
    },
    decimal: {
      style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2
    },
    percent: {
      style: 'percent', useGrouping: false
    }
  },
  'de-DE': {
    currency: {
      style: 'currency', currency: 'EUR', useGrouping: true
    },
    decimal: {
      style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2
    },
    percent: {
      style: 'percent', useGrouping: false
    }
  },
  'en': {
    currency: {
      style: 'currency', currency: 'SEK'
    }
  }
}

// Create VueI18n instance with options
let vueI18n = new VueI18n({
  locale: supportedLocale,
  messages,
  dateTimeFormats: {
    'sv-SE': dateTimeFormats,
    'nb-NO': dateTimeFormats,
    'de-DE': dateTimeFormats,
  },
  numberFormats,
});

async function loadTranslations(locale: string) {
  locale = locale || getSupportedLocale();
  const localeMessages = await get(`${ENDPOINTS.TRANSLATIONS}/${ENDPOINTS.AS_JSON}/${locale}`);

  // check that we received something that looks valid
  if (Object.keys(localeMessages).length > minimumTranslationLength) {
    messages[locale] = Object.assign(localeMessages);
  }
}

function getUserLanguage() {
  return this.vueI18n.locale
}

async function setLanguage(vueInstance: Vue, locale: string) {
  if (locale !== this.vueI18n.locale) {
    if (locale === 'sv-SE') {
      vueInstance['$date'].locale(locale_sv);
    } else if (locale === 'nb-NO') {
      vueInstance['$date'].locale(locale_nb);
    } else if (locale === 'de-DE') {
      vueInstance['$date'].locale(locale_de);
    } else if (locale === 'en') {
      vueInstance['$date'].locale(locale_en);
    } else {
      vueInstance['$date'].locale('sv-SE');
    }

    this.vueI18n.locale = locale;
    await loadTranslations(locale);
  }
}

function translateEntityName(entity: TranslatableEntity) {
  return this.vueI18n.t(entity.nameTranslationKey);
}

function translateEntityDescription(entity: TranslatableEntity) {
  return this.vueI18n.t(entity.descriptionTranslationKey);
}

export default {
  vueI18n,
  getUserLanguage,
  getSupportedLocale,
  loadTranslations,
  setLanguage,
  translateEntityName,
  translateEntityDescription,
}
